import { Component, OnInit, Type, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { WizardPlugin } from '../../../../decorators/wizard-plugin';
import { UserAccountData, AccessLevel } from '../../../../models/wizard-plugin-models/user-account-data';
import { IWizardPluginComponent } from '../../../../interfaces/wizard-plugin-component.interface';
import { DecoratorConfig } from '../../../../decoration-base/decorator-config';
import { IPluginPreviewComponent } from '../../../../interfaces/plugin-preview-component.interface';
import { BasePluginData } from '../../../../models/wizard-plugin-models/base-plugin-data';
import { ResourceMasterDataService } from '../../../../services/resource-master-data.service';
import { Pair } from '../../../../models/pair';
import { Log, IShortLogger } from '../../../../services/logger.service';

@WizardPlugin({
  name: "User Accounts",
  optional: false,
  description: "Configure user accounts, roles, and permissions for cloud resources",
  dataModelType: UserAccountData
})
@Component({
  selector: 'app-user-account-plugin',
  templateUrl: './user-account-plugin.component.html',
  styleUrl: './user-account-plugin.component.css'
})
export class UserAccountPluginComponent implements IWizardPluginComponent, OnInit, AfterViewInit {
  private readonly logger: IShortLogger<UserAccountPluginComponent> = Log.create(UserAccountPluginComponent);

  userAccountData: UserAccountData = new UserAccountData();

  // UI state properties
  selectedCloudProvider: string = '';
  selectedAccessLevel: string = '';

  // Options for UI components
  cloudProviderOptions: Pair[] = [];

  // Track initialization state
  private isInitialized = false;

  constructor(
    private masterDataService: ResourceMasterDataService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    try {
      this.loadCloudProviderOptions();
      this.loadUserAccountDataFromMasterData();

      // Set default values
      this.setDefaultValues();

      // Mark as initialized
      this.isInitialized = true;

      this.logger.i('UserAccountPlugin initialized successfully');
    } catch (error) {
      this.logger.e('Error initializing UserAccountPluginComponent:', error);
    }
  }

  ngAfterViewInit(): void {
    // Initialize Bootstrap tooltips after view initialization
    this.initializeTooltips();
  }

  private loadCloudProviderOptions(): void {
    try {
      // Only show Azure as the cloud provider option
      this.cloudProviderOptions = [
        { id: 'azure', name: 'Microsoft Azure' }
      ];
    } catch (error) {
      this.logger.e('Error loading cloud provider options:', error);
      this.cloudProviderOptions = [
        { id: 'azure', name: 'Microsoft Azure' }
      ];
    }
  }

  private loadUserAccountDataFromMasterData(): void {
    try {
      // Load data from master data service if available
      const userAccountsData = this.masterDataService?.get('userAccounts') as Partial<UserAccountData> | undefined;

      if (userAccountsData) {
        if (userAccountsData.accessLevels) {
          this.userAccountData.accessLevels = userAccountsData.accessLevels;
        }
        if (userAccountsData.providerMappings) {
          this.userAccountData.providerMappings = userAccountsData.providerMappings;
        }
      }
    } catch (error) {
      this.logger.e('Error loading user account data from master data:', error);
    }
  }

  private initializeTooltips(): void {
    try {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        // Initialize Bootstrap tooltips
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        tooltipTriggerList.forEach(tooltipTriggerEl => {
          // Check if Bootstrap tooltip is available
          if ((window as any).bootstrap && (window as any).bootstrap.Tooltip) {
            new (window as any).bootstrap.Tooltip(tooltipTriggerEl);
          }
        });
      }, 100);
    } catch (error) {
      this.logger.e('Error initializing tooltips:', error);
    }
  }

  private setDefaultValues(): void {
    try {
      // Set default cloud provider
      if (this.cloudProviderOptions.length > 0 && !this.selectedCloudProvider) {
        this.selectedCloudProvider = this.cloudProviderOptions[0].id;
      }

      // Set default access level
      if (this.userAccountData.accessLevels.length > 0 && !this.selectedAccessLevel) {
        this.selectedAccessLevel = this.userAccountData.accessLevels[0].id;
      }
    } catch (error) {
      this.logger.e('Error setting default values:', error);
    }
  }

  selectCloudProvider(providerId: string): void {
    try {
      this.selectedCloudProvider = providerId;
      this.onCloudProviderChange();
    } catch (error) {
      this.logger.e('Error selecting cloud provider:', error);
    }
  }

  selectAccessLevel(accessLevelId: string): void {
    try {
      this.selectedAccessLevel = accessLevelId;
    } catch (error) {
      this.logger.e('Error selecting access level:', error);
    }
  }

  onCloudProviderChange(): void {
    try {
      // Update available options based on selected cloud provider
      this.logger.i('Cloud provider changed to:', this.selectedCloudProvider);
    } catch (error) {
      this.logger.e('Error handling cloud provider change:', error);
    }
  }

  // Interface implementations
  getPluginData(): BasePluginData {
    try {
      // Create a new UserAccountData instance with only selected data
      const selectedData = new UserAccountData();

      // Only include selected access level
      const selectedAccessLevel = this.userAccountData.accessLevels.find(al => al.id === this.selectedAccessLevel);
      selectedData.accessLevels = selectedAccessLevel ? [selectedAccessLevel] : [];

      // Only include selected cloud provider mapping
      if (this.selectedCloudProvider && this.userAccountData.providerMappings[this.selectedCloudProvider]) {
        selectedData.providerMappings = {
          [this.selectedCloudProvider]: this.userAccountData.providerMappings[this.selectedCloudProvider]
        };
      }

      // Set the selected values for reference
      selectedData.selectedCloudProvider = this.selectedCloudProvider;
      selectedData.selectedAccessLevel = this.selectedAccessLevel;

      this.logger.i('Selected plugin data:', selectedData);
      return selectedData;
    } catch (error) {
      this.logger.e('Error getting plugin data:', error);
      return new UserAccountData();
    }
  }

  setPluginData(data: BasePluginData): void {
    try {
      if (data instanceof UserAccountData) {
        this.userAccountData = data;
        this.logger.i('Plugin data set:', this.userAccountData);
      }
    } catch (error) {
      this.logger.e('Error setting plugin data:', error);
    }
  }

  validate(): boolean {
    try {
      const hasRequiredSelections = !!(this.selectedCloudProvider && this.selectedAccessLevel);
      return hasRequiredSelections;
    } catch (error) {
      this.logger.e('Error validating plugin data:', error);
      return false;
    }
  }

  isComplete(): boolean {
    return this.validate();
  }

  reset(): void {
    try {
      this.userAccountData = new UserAccountData();
      this.selectedCloudProvider = '';
      this.selectedAccessLevel = '';

      this.setDefaultValues();
    } catch (error) {
      this.logger.e('Error resetting user account plugin:', error);
    }
  }

  asPreview?(): Type<IPluginPreviewComponent> {
    // TODO: Implement preview component
    throw new Error('Preview component not implemented yet.');
  }

  getSelectedCloudProviderName(): string {
    const provider = this.cloudProviderOptions.find(p => p.id === this.selectedCloudProvider);
    return provider ? provider.name : '';
  }
}
